import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, nullableText } from './common.js';
import { ShipmentTypeSchema, PublicStageSchema } from './enums.js';
import { MoneySchema } from './money.js';
import { OrderStatusCodeSchema, ShipmentStatusHistoryResponseSchema } from './statuses.js';
/**
 * Jo'natma — LOGISTIKA BIRLIGI. Tovarlar buyurtmaga emas, jo'natmaga tegishli:
 * bitta buyurtmada do'kondan ertaga ketadigan moy va Xitoydan 18 kunda
 * keladigan bamper bo'lishi mumkin (docs/02-MAHSULOT.md).
 */
/** Buyurtma qatori — SNAPSHOT. Katalog keyin o'zgarsa ham bu o'zgarmaydi. */
export const OrderItemResponseSchema = z.object({
    id: IdSchema,
    productId: IdSchema,
    qty: z.number().int().positive(),
    unitPrice: MoneySchema,
    lineTotal: MoneySchema,
    productName: z.string(),
    productSku: z.string(),
    brandName: z.string(),
    imageUrl: z.string().nullable(),
    categoryIconKey: nullableText(32),
    /** Faqat `report.finance` ruxsati borlarga qaytariladi. */
    unitCost: MoneySchema.optional(),
});
export const ShipmentResponseSchema = z.object({
    id: IdSchema,
    orderId: IdSchema,
    type: ShipmentTypeSchema,
    statusCode: OrderStatusCodeSchema,
    statusLabelUz: z.string(),
    statusLabelRu: nullableText(80),
    publicStage: PublicStageSchema,
    shopId: IdSchema.nullable(),
    shopNameUz: z.string().nullable(),
    consignmentId: IdSchema.nullable(),
    etaFrom: IsoDateTimeSchema.nullable(),
    etaTo: IsoDateTimeSchema.nullable(),
    /** Ko'p siljish — ta'minotchi muammosi signali, admin ko'radi. */
    etaShiftCount: z.number().int().nonnegative(),
    trackingNo: nullableText(64),
    courierId: IdSchema.nullable(),
    deliveredAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    items: z.array(OrderItemResponseSchema),
    itemsTotal: MoneySchema,
});
/** Uchinchi qatlam — to'liq treker. Foydalanuvchilarning ko'pchiligi bu yerga kirmaydi. */
export const ShipmentDetailResponseSchema = ShipmentResponseSchema.extend({
    history: z.array(ShipmentStatusHistoryResponseSchema),
});
/**
 * Jo'natmani do'konga biriktirish.
 *
 * Do'konni TIZIM TANLAMAYDI: do'konlararo avtomatik marshrutlash
 * qilinmaydi (docs/02-MAHSULOT.md). Operator qaysi do'kondan jo'natishni
 * telefonda hal qiladi va shu yerda belgilaydi. Qoldiq rezervi aynan shu
 * do'kondan qo'yiladi, shuning uchun `CONFIRMED` dan oldin to'ldirilishi shart.
 */
export const UpdateShipmentSchema = z
    .object({
    shopId: IdSchema.nullable().optional(),
    trackingNo: z.string().trim().max(64).nullable().optional(),
})
    .strict();
export const AssignCourierSchema = z
    .object({
    courierId: IdSchema,
    etaFrom: IsoDateTimeSchema,
    etaTo: IsoDateTimeSchema.optional(),
    trackingNo: z.string().trim().max(64).optional(),
})
    .strict();
/* ============================== Partiya ================================ */
/**
 * Xitoy partiyasi. Xitoydan tovar bitta-bitta kelmaydi: bitta yukda 40 ta
 * buyurtmaning qismlari, bitta invoys, bitta YBD. Partiya statusini
 * o'zgartirish ICHIDAGI BARCHA jo'natmalarni ko'chiradi (ommaviy amal,
 * sikl ichida bitta-bitta emas — avtopart-queue skill).
 */
export const ConsignmentResponseSchema = z.object({
    id: IdSchema,
    invoiceNo: z.string(),
    supplierId: IdSchema.nullable(),
    supplierName: z.string().nullable(),
    statusCode: OrderStatusCodeSchema,
    statusLabelUz: z.string(),
    carrier: nullableText(80),
    declarationNo: nullableText(64),
    borderPoint: nullableText(80),
    etaFrom: IsoDateTimeSchema.nullable(),
    etaTo: IsoDateTimeSchema.nullable(),
    /** Tannarxga kiradi — faqat `report.finance` ruxsati borlarga. */
    customsFee: MoneySchema.optional(),
    freightFee: MoneySchema.optional(),
    note: nullableText(500),
    shipmentCount: z.number().int().nonnegative(),
    createdAt: IsoDateTimeSchema,
    closedAt: IsoDateTimeSchema.nullable(),
});
/**
 * Partiya ichidagi bitta jo'natma.
 *
 * Operator butun partiyani bir bosishda ko'chirishdan OLDIN ichida
 * nima borligini ko'rishi kerak: 40 ta jo'natmani ko'r-ko'rona
 * ko'chirish — orqaga qaytarib bo'lmaydigan amal. Ro'yxatda faqat
 * SANOQ bor edi.
 */
export const ConsignmentShipmentSchema = z.object({
    shipmentId: IdSchema,
    orderId: IdSchema,
    orderNumber: z.string(),
    customerName: z.string(),
    regionNameUz: z.string().nullable(),
    statusCode: OrderStatusCodeSchema,
    statusLabelUz: z.string(),
    statusLabelRu: nullableText(80),
    /** Tovar nomlari — qatorda qisqartirib ko'rsatiladi. */
    productNames: z.array(z.string()),
});
/** Bitta partiya — ichidagi jo'natmalar bilan. */
export const ConsignmentDetailResponseSchema = ConsignmentResponseSchema.extend({
    shipments: z.array(ConsignmentShipmentSchema),
});
export const CreateConsignmentSchema = z
    .object({
    invoiceNo: z.string().trim().min(1).max(64),
    supplierId: IdSchema.optional(),
    carrier: z.string().trim().max(80).optional(),
    declarationNo: z.string().trim().max(64).optional(),
    borderPoint: z.string().trim().max(80).optional(),
    etaFrom: IsoDateTimeSchema.optional(),
    etaTo: IsoDateTimeSchema.optional(),
    customsFee: MoneySchema.optional(),
    freightFee: MoneySchema.optional(),
    note: z.string().trim().max(500).optional(),
})
    .strict();
export const UpdateConsignmentSchema = CreateConsignmentSchema.partial().strict();
/**
 * Partiya statusini ommaviy o'zgartirish natijasi.
 *
 * Partiyadagi jo'natmalar har doim ham bir xil statusda bo'lavermaydi
 * (masalan yangi jo'natma yo'l o'rtasida biriktirilgan bo'lishi mumkin).
 * Shuning uchun natija ko'chganlarni va KO'CHMAGANLARNI SABABI bilan
 * qaytaradi — jimgina tashlab ketilmaydi.
 */
export const ConsignmentStatusResultSchema = z.object({
    consignmentId: IdSchema,
    toCode: z.string(),
    movedCount: z.number().int().nonnegative(),
    notifiedCount: z.number().int().nonnegative(),
    skipped: z.array(z.object({
        shipmentId: IdSchema,
        fromCode: z.string(),
        reason: z.enum(['NO_TRANSITION', 'FORBIDDEN']),
    })),
});
export const AttachShipmentsToConsignmentSchema = z
    .object({ shipmentIds: z.array(IdSchema).min(1) })
    .strict();
/* ============================ Ta'minotchi ============================== */
export const SupplierResponseSchema = z.object({
    id: IdSchema,
    name: z.string(),
    country: nullableText(60),
    contact: nullableText(160),
    note: nullableText(500),
    isActive: z.boolean(),
});
export const CreateSupplierSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    country: z.string().trim().max(60).optional(),
    contact: z.string().trim().max(160).optional(),
    note: z.string().trim().max(500).optional(),
})
    .strict();
//# sourceMappingURL=shipment.js.map